import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksPage } from '../../components/liks/LiksPage/LiksPage';
import { LiksRefreshButton } from '../../components/liks/LiksRefreshButton/LiksRefreshButton';
import { LiksSearch } from '../../components/liks/LiksSearch/LiksSearch';
import { useI18n } from '../../i18n/useI18n';
import { useCreateKnowledgeDatabaseMutation } from '../../features/knowledge/mutations/useKnowledgeDatabaseMutations';
import { useKnowledgeDatabasesQuery } from '../../features/knowledge/queries/useKnowledgeDatabasesQuery';
import { resolveKnowledgeDatabaseTabPath } from '../../features/knowledge/knowledgeTabs';
import { useKnowledgeDatabasesPageStore } from '../../features/knowledge/stores/useKnowledgeDatabasesPageStore';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import { useUserConfig } from '../../userConfig/useUserConfig';
import { CreateKnowledgeDatabaseDialog } from './CreateKnowledgeDatabaseDialog';
import { compareDatabasesByLastModifiedDesc } from './compareDatabasesByLastModifiedDesc';
import { KnowledgePageDatabaseList } from './KnowledgePageDatabaseList';
import { formatTimestamp } from '../../utils/formatUtils';
import './KnowledgePage.css';

export function KnowledgePage() {
  const { locale, translate } = useI18n();
  const navigate = useNavigate();
  const databasesQuery = useKnowledgeDatabasesQuery();
  const createDatabaseMutation = useCreateKnowledgeDatabaseMutation();
  const {
    isUserConfigReady,
    pinnedKnowledgeDatabaseIds,
    setPinnedKnowledgeDatabaseIds,
  } = useUserConfig();
  const [searchQuery, setSearchQuery] = useState('');
  const [isPinnedGroupExpanded, setIsPinnedGroupExpanded] = useState(true);
  const isCreateDialogOpen = useKnowledgeDatabasesPageStore(
    (state) => state.isCreateDialogOpen,
  );
  const openCreateDialog = useKnowledgeDatabasesPageStore(
    (state) => state.openCreateDialog,
  );
  const closeCreateDialog = useKnowledgeDatabasesPageStore(
    (state) => state.closeCreateDialog,
  );

  const databases = databasesQuery.data ?? [];
  const isLoadingDatabases = databasesQuery.isLoading;
  const databasesError = databasesQuery.isError;
  const isPageLoading = isLoadingDatabases || !isUserConfigReady;
  const pinnedIdSet = new Set(pinnedKnowledgeDatabaseIds);
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredDatabases = (
    normalizedSearch === ''
      ? databases
      : databases.filter((database) => {
          const name = database.name?.toLowerCase() ?? '';
          const description = database.description?.toLowerCase() ?? '';
          return (
            name.includes(normalizedSearch) ||
            description.includes(normalizedSearch)
          );
        })
  )
    .slice()
    .sort(compareDatabasesByLastModifiedDesc);

  const pinnedDatabases = filteredDatabases.filter(
    (database) => database.id != null && pinnedIdSet.has(database.id),
  );
  const unpinnedDatabases = filteredDatabases.filter(
    (database) => database.id == null || !pinnedIdSet.has(database.id),
  );

  useEffect(() => {
    if (!isUserConfigReady || !databasesQuery.isSuccess) {
      return;
    }

    const accessibleIds = new Set(
      (databasesQuery.data ?? [])
        .map((database) => database.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    );
    const prunedIds = pinnedKnowledgeDatabaseIds.filter((id) =>
      accessibleIds.has(id),
    );

    if (prunedIds.length !== pinnedKnowledgeDatabaseIds.length) {
      setPinnedKnowledgeDatabaseIds(prunedIds);
    }
  }, [
    databasesQuery.data,
    databasesQuery.isSuccess,
    isUserConfigReady,
    pinnedKnowledgeDatabaseIds,
    setPinnedKnowledgeDatabaseIds,
  ]);

  const formatLastModified = (value: string | null | undefined) =>
    value
      ? formatTimestamp(value, locale)
      : translate('knowledge.notAvailable');

  const handleTogglePin = (databaseId: string) => {
    if (pinnedIdSet.has(databaseId)) {
      setPinnedKnowledgeDatabaseIds(
        pinnedKnowledgeDatabaseIds.filter((id) => id !== databaseId),
      );
      return;
    }

    setPinnedKnowledgeDatabaseIds([...pinnedKnowledgeDatabaseIds, databaseId]);
  };

  const handleManualRefresh = () => {
    void databasesQuery.refetch();
  };

  const handleCreate = ({
    name,
    description,
    icon,
  }: {
    name: string;
    description: string;
    icon: string;
  }) => {
    createDatabaseMutation.mutate(
      { name, description, icon },
      {
        onSuccess: (database) => {
          toast.success({
            title: translate('knowledge.createSuccessTitle'),
            description: translate('knowledge.createSuccessDescription', {
              title: name,
            }),
          });
          closeCreateDialog();
          if (database.id) {
            navigate(resolveKnowledgeDatabaseTabPath(database.id, 'documents'));
          }
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.createErrorTitle'),
            translate('knowledge.createErrorDescription'),
          );
        },
      },
    );
  };

  const handleOpenDatabase = (databaseId: string) => {
    navigate(resolveKnowledgeDatabaseTabPath(databaseId, 'documents'));
  };

  return (
    <>
      <LiksPage
        header={
          <header className="knowledge-page__header">
            <h1>{translate('knowledge.title')}</h1>
            <div className="knowledge-page__toolbar">
              <LiksSearch
                className="knowledge-page__toolbar-search"
                variant="on-light"
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={translate('knowledge.searchDatabasesPlaceholder')}
                clearLabel={translate('common.search.clear')}
                aria-label={translate('knowledge.searchDatabasesLabel')}
              />
              <LiksButton
                className="knowledge-page__create-button"
                onClick={openCreateDialog}
              >
                <Plus strokeWidth={2} aria-hidden />
                {translate('knowledge.createDatabase')}
              </LiksButton>
              <LiksRefreshButton
                className="knowledge-page__refresh-button"
                onRefresh={handleManualRefresh}
              />
            </div>
          </header>
        }
        content={
          <div className="knowledge-page__content">
            <KnowledgePageDatabaseList
              isPageLoading={isPageLoading}
              databasesError={databasesError}
              databases={databases}
              filteredDatabases={filteredDatabases}
              pinnedDatabases={pinnedDatabases}
              unpinnedDatabases={unpinnedDatabases}
              isPinnedGroupExpanded={isPinnedGroupExpanded}
              translate={translate}
              formatLastModified={formatLastModified}
              onTogglePinnedGroup={() =>
                setIsPinnedGroupExpanded((expanded) => !expanded)
              }
              onTogglePin={handleTogglePin}
              onOpenDatabase={handleOpenDatabase}
            />
          </div>
        }
      />

      {isCreateDialogOpen && (
        <CreateKnowledgeDatabaseDialog
          titleLabel={translate('knowledge.form.titleLabel')}
          descriptionLabel={translate('knowledge.form.descriptionLabel')}
          iconLabel={translate('knowledge.form.iconLabel')}
          iconSearchPlaceholder={translate(
            'knowledge.iconPicker.searchPlaceholder',
          )}
          iconSearchClearLabel={translate('common.search.clear')}
          iconNoResultsLabel={translate('knowledge.iconPicker.noResults')}
          getIconOptionLabel={(iconName) =>
            translate('knowledge.iconPicker.optionLabel', { icon: iconName })
          }
          titlePlaceholder={translate('knowledge.form.titlePlaceholder')}
          descriptionPlaceholder={translate(
            'knowledge.form.descriptionPlaceholder',
          )}
          createLabel={translate('knowledge.createDatabase')}
          cancelLabel={translate('knowledge.form.cancel')}
          closeLabel={translate('knowledge.form.close')}
          onCreate={handleCreate}
          onClose={closeCreateDialog}
        />
      )}
    </>
  );
}
